// Styles
import "./workspace.scss";
// Tools
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../shared/store/store";
// Components
import { Dashboard } from "./components/dashboard/Dashboard";
import { Chat } from "./components/chat/Chat";
// Utils
import { SplitPane } from "./utils/split-pane/splitPane";
import { filterComponents } from "./utils/component-utils/filter-components";
import { getComponentsWeights } from "./utils/component-utils/get-components-weights";
import { getComponentsFromStorage } from "./utils/component-utils/get-components-from-storage";
import { saveConfigInLocalStorageIfNeeded } from "./utils/module-utils/save-config";

export const Workspace = () => {
	// Список модулей, получаемых с сервера
	const appModules = useSelector(
		(state: RootState) => state.user?.modules ?? [],
	);
	// Стартовый конфиг модулей
	const appModulesConfig = useSelector(
		(state: RootState) => state.app.modulesConfig,
	);
	// Стартовый конфиг секций
	const appSectionsConfig = useSelector(
		(state: RootState) => state.app.sectionsConfig,
	);
	const layoutHardResetNonce = useSelector(
		(state: RootState) => state.app.layoutHardResetNonce,
	);
	const workspaceSplitResetNonce = useSelector(
		(state: RootState) => state.app.workspaceSplitResetNonce,
	);

	// Собираем компоненты с помощью утилиты — передаём маппинг доступных нод
	const components = filterComponents(appModules, {
		dashboard: <Dashboard />,
		chat: <Chat />,
	});

	// Сохраняем даннные в localStorage при первой загрузке компонента
	useEffect(() => {
		saveConfigInLocalStorageIfNeeded(appModulesConfig);
	}, [appModulesConfig]);

	// Собираем простой массив panes в порядке appModules
	// Важно: сохраняем ключи элементов (component.key) — они используются для сохранения размеров
	const panes = components.map((c) => {
		const node = c.node as React.ReactElement;
		if (React.isValidElement(node)) {
			return React.cloneElement(node, { key: c.key });
		}
		// Если node не является React-элементом — оборачиваем в Fragment с ключом
		return <React.Fragment key={c.key}>{c.node}</React.Fragment>;
	});

	// Берём конфиг секций — сначала из localStorage, потом из Redux store
	const appSectionsConfigFromStorage = getComponentsFromStorage();
	const appSectionsConfigToUse =
		appSectionsConfigFromStorage ?? appSectionsConfig;

	// Скрываем секции с sectionWeight === 0 (режимы "только дашборды" / "только чат")
	const visiblePanes = panes.filter((p) => {
		const key = String((p as React.ReactElement)?.key ?? "");
		const sectionCfg = (appSectionsConfigToUse ?? []).find(
			(s: any) => s.sectionName === key,
		);
		return !sectionCfg || Number(sectionCfg.sectionWeight) !== 0;
	});

	const sectionWeightSig = JSON.stringify(appSectionsConfigToUse ?? []);
	const visiblePaneKeysSig = visiblePanes
		.map((p) => String((p as React.ReactElement)?.key ?? ""))
		.join(",");

	// Берём начальные проценты из config секций (sectionsConfig) если есть
	const initialPercentsFromSections = useMemo(
		() => getComponentsWeights(appSectionsConfigToUse as never, visiblePanes),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[sectionWeightSig, visiblePaneKeysSig, layoutHardResetNonce],
	);

	// На случай, если не посчиаются проценты
	const initialPercentsFallback = (appModulesConfig as any)?.layout
		?.initialPercents;
	const initialPercents =
		initialPercentsFromSections && initialPercentsFromSections.length
			? initialPercentsFromSections
			: initialPercentsFallback;

	if (visiblePanes.length === 0) {
		return <div className='workspace' />;
	}

	return (
		<div className='workspace'>
			<SplitPane
				key={`workspace-${layoutHardResetNonce}-${workspaceSplitResetNonce}`}
				panes={visiblePanes}
				direction='vertical'
				initialPercents={
					initialPercents && initialPercents.length
						? initialPercents
						: undefined
				}
			/>
		</div>
	);
};
